import React, { useEffect, useState, useCallback } from "react";
import {
  Loader2,
  RefreshCw,
  Users,
  Handshake,
  TrendingUp,
  Clock,
  Cross,
  PenLine,
  Cloud,
  CheckCircle2,
} from "lucide-react";
import { Button } from "../ui/button";
import { processService } from "../../services/process.service";
import { formatCurrencyBRL } from "../../lib/utils";

const ACEITE_COLORS = ["#6366f1", "#e2e8f0"];

function KpiCard({
  icon: Icon,
  label,
  value,
  sub,
  colorClass = "text-foreground",
}) {
  return (
    <div className="bg-card rounded-xl border border-border p-5 flex flex-col gap-2 shadow-sm">
      <div className="flex items-center gap-2 text-muted-foreground text-sm">
        <Icon className="w-4 h-4" />
        {label}
      </div>
      <p className={`text-3xl font-bold font-mono tabular-nums ${colorClass}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mt-6 mb-3">
      {children}
    </h3>
  );
}

function FinancialRow({ label, value, highlight }) {
  return (
    <div
      className={`flex justify-between items-center py-2 border-b border-border last:border-0 ${highlight ? "font-semibold" : ""}`}
    >
      <span className="text-sm text-muted-foreground">{label}</span>
      <span
        className={`text-sm ${highlight ? "text-foreground" : "text-muted-foreground"}`}
      >
        <span className="font-mono tabular-nums">{formatCurrencyBRL(value)}</span>
      </span>
    </div>
  );
}


export function DashboardTab({ processoId, stats: externalStats, temValores = true }) {
  const [fetchedStats, setFetchedStats] = useState(null);
  const [loading, setLoading] = useState(!externalStats);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (externalStats) return;
    setLoading(true);
    setError(null);
    try {
      const data = await processService.getDashboardStats(processoId);
      setFetchedStats(data);
    } catch (e) {
      setError("Não foi possível carregar o dashboard.");
    } finally {
      setLoading(false);
    }
  }, [processoId, externalStats]);

  useEffect(() => {
    load();
  }, [load]);

  const stats = externalStats ?? fetchedStats;

  if (loading) {
    return (
      <div className="flex justify-center items-center py-24 text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Carregando
        dashboard...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center py-24 gap-3 text-muted-foreground">
        <p>{error}</p>
        <Button variant="outline" onClick={load}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Tentar novamente
        </Button>
      </div>
    );
  }

  const {
    total,
    total_acordo,
    total_cessao,
    total_sem_resposta,
    total_falecidos,
    acordo,
    cessao,
    assinaturas,
  } = stats;

  const assinaturasManual = assinaturas?.manual ?? 0;
  const assinaturasProvider = assinaturas?.provider ?? 0;
  const assinaturasTotal = assinaturas?.total ?? (assinaturasManual + assinaturasProvider);

  const hasCessao = total_cessao > 0;
  const totalResponderam = total_acordo + total_cessao;
  const pctCessao =
    total > 0 ? ((total_cessao / total) * 100).toFixed(1) : "0.0";
  const pctResponderam =
    total > 0 ? ((totalResponderam / total) * 100).toFixed(1) : "0.0";

  const aceiteData = [
    { name: "Aceitaram o Acordo", value: totalResponderam },
    { name: "Sem resposta", value: total_sem_resposta },
  ].filter((d) => d.value > 0);

  const financialBarData = [
    {
      name: "Proposta\nTotal",
      value: acordo.proposta_total + cessao.proposta_total,
      fill: "#6366f1",
    },
    hasCessao && {
      name: "Crédito\nAdquirido",
      value: cessao.credito_total - (cessao.fgts_total ?? 0),
      fill: "#f59e0b",
    },
    hasCessao && {
      name: "Propostas\nAceitas",
      value: cessao.proposta_total,
      fill: "#22c55e",
    },
    hasCessao && {
      name: "Desembolso\nTotal",
      value: cessao.proposta_total + cessao.sindicato_total + cessao.contador_total,
      fill: "#0ea5e9",
    },
  ].filter(Boolean);

  return (
    <div className="space-y-2 pb-8">
      <div className="flex justify-between items-center">
        <SectionTitle>Resumo</SectionTitle>
        {!externalStats && (
          <Button
            variant="ghost"
            size="icon"
            onClick={load}
            className="h-8 w-8 text-muted-foreground hover:text-muted-foreground"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* KPI Cards */}
      <div className={`grid gap-4 ${hasCessao ? "grid-cols-2 md:grid-cols-5" : "grid-cols-2 md:grid-cols-4"}`}>
        <KpiCard icon={Users} label="Total de Substituídos" value={total} />
        <KpiCard
          icon={Handshake}
          label="Aceitaram"
          value={totalResponderam}
          sub={`${pctResponderam}% do total`}
          colorClass="text-statusInfo"
        />
        {hasCessao && (
          <KpiCard
            icon={TrendingUp}
            label="Aceitaram a Cessão"
            value={total_cessao}
            sub={`${pctCessao}% do total`}
            colorClass="text-statusWarning"
          />
        )}
        <KpiCard
          icon={Clock}
          label="Sem Resposta"
          value={total_sem_resposta}
          sub={`${total > 0 ? ((total_sem_resposta / total) * 100).toFixed(1) : 0}% do total`}
          colorClass="text-muted-foreground"
        />
        <KpiCard
          icon={Cross}
          label="Falecidos"
          value={total_falecidos}
          colorClass="text-muted-foreground"
        />
      </div>

      {/* Assinaturas — separação por origem */}
      <SectionTitle>Assinaturas</SectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard
          icon={CheckCircle2}
          label="Total de Assinaturas"
          value={assinaturasTotal}
          sub={total > 0 ? `${((assinaturasTotal / total) * 100).toFixed(1)}% dos substituídos` : undefined}
          colorClass="text-statusSuccess"
        />
        <KpiCard
          icon={Cloud}
          label="Via Provider"
          value={assinaturasProvider}
          sub={assinaturasTotal > 0 ? `${((assinaturasProvider / assinaturasTotal) * 100).toFixed(1)}% das assinaturas` : "Sem assinaturas"}
          colorClass="text-statusInfo"
        />
        <KpiCard
          icon={PenLine}
          label="Manuais"
          value={assinaturasManual}
          sub={assinaturasTotal > 0 ? `${((assinaturasManual / assinaturasTotal) * 100).toFixed(1)}% das assinaturas` : "Sem assinaturas"}
          colorClass="text-statusWarning"
        />
      </div>

      {assinaturasTotal > 0 && (
        <div className="bg-card rounded-xl border border-border p-5 shadow-sm mt-3">
          <p className="text-xs font-medium text-muted-foreground mb-3">Distribuição por origem</p>
          <div className="space-y-3">
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <Cloud className="h-3.5 w-3.5 text-sky-500" />
                  Via Provider (ClickSign/ZapSign)
                </span>
                <span className="font-medium font-mono tabular-nums">{assinaturasProvider}</span>
              </div>
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full transition-all bg-sky-500"
                  style={{ width: `${assinaturasTotal > 0 ? (assinaturasProvider / assinaturasTotal) * 100 : 0}%` }}
                />
              </div>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <PenLine className="h-3.5 w-3.5 text-amber-500" />
                  Manual (registrada pelo admin)
                </span>
                <span className="font-medium font-mono tabular-nums">{assinaturasManual}</span>
              </div>
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full transition-all bg-primary"
                  style={{ width: `${assinaturasTotal > 0 ? (assinaturasManual / assinaturasTotal) * 100 : 0}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Charts Row */}
      <SectionTitle>Distribuição</SectionTitle>
      <div className={`grid grid-cols-1 gap-4 ${temValores ? "md:grid-cols-2" : ""}`}>
        {/* Aceite Pie */}
        <div className="bg-card rounded-xl border border-border p-5 shadow-sm">
          <p className="text-sm font-medium text-muted-foreground mb-4">
            Tipo de Aceite
          </p>
          {total === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-8">Sem dados</p>
          ) : (
            <div className="space-y-3">
              {aceiteData.map((d, i) => (
                <div key={d.name} className="space-y-1">
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{d.name}</span>
                    <span className="font-medium font-mono tabular-nums">{d.value}</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${total > 0 ? (d.value / total) * 100 : 0}%`,
                        backgroundColor: ACEITE_COLORS[i],
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-center text-muted-foreground mt-1">
            <span className="font-mono tabular-nums">{pctResponderam}%</span> responderam{hasCessao && totalResponderam > 0 ? <> · dos quais <span className="font-mono tabular-nums">{((total_cessao / totalResponderam) * 100).toFixed(1)}%</span> optaram pela cessão</> : ""}
          </p>
        </div>

        {/* Financial Detail cards — hidden when temValores is false */}
        {temValores && (
          <div className="flex flex-col gap-3">
            <div className="bg-card rounded-xl border border-border p-4 shadow-sm">
              <p className="text-xs font-semibold text-statusInfo mb-2">
                Consolidado (<span className="font-mono tabular-nums">{total_acordo + total_cessao}</span> substituídos)
              </p>
              <FinancialRow
                label="Crédito Bruto Total"
                value={acordo.credito_total + cessao.credito_total}
                highlight
              />
            </div>
            {hasCessao && (
              <div className="bg-card rounded-xl border border-border p-4 shadow-sm">
                <p className="text-xs font-semibold text-statusWarning mb-2">
                  Cessão (<span className="font-mono tabular-nums">{total_cessao}</span> substituídos)
                </p>
                <FinancialRow
                  label="Crédito Bruto Total"
                  value={cessao.credito_total}
                />
                <FinancialRow
                  label="FGTS (direto ao trabalhador)"
                  value={cessao.fgts_total ?? 0}
                />
                <FinancialRow
                  label="Crédito Adquirido (Bruto − FGTS)"
                  value={cessao.credito_total - (cessao.fgts_total ?? 0)}
                  highlight
                />
                <FinancialRow
                  label="Proposta Final Total"
                  value={cessao.proposta_total}
                />
                <div className="flex justify-between items-center py-2">
                  <span className="text-sm text-muted-foreground">Deságio Médio</span>
                  <span className="text-sm text-muted-foreground">
                    <span className="font-mono tabular-nums">{cessao.desagio_medio.toFixed(2)}%</span>
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Financial Bar Chart — hidden when temValores is false */}
      {temValores && (
        <>
          <SectionTitle>Valores Consolidados</SectionTitle>
          <div className="bg-card rounded-xl border border-border p-5 shadow-sm">
            <p className="text-sm font-medium text-muted-foreground mb-4">
              Visão geral dos valores consolidados
            </p>
            {financialBarData.map(d => (
              <FinancialRow
                key={d.name}
                label={d.name.replace("\n", " ")}
                value={d.value}
              />
            ))}
          </div>

          {/* Fee totals */}
          <SectionTitle>Taxas e Deduções</SectionTitle>
          <div className="bg-card rounded-xl border border-border p-5 shadow-sm">
            <FinancialRow
              label={hasCessao ? "Honorários Sindicato (Total)" : "Honorários Sindicato"}
              value={acordo.sindicato_total + cessao.sindicato_total}
            />
            <FinancialRow
              label={hasCessao ? "Honorários Contador (Total)" : "Honorários Contador"}
              value={acordo.contador_total + cessao.contador_total}
            />
            {hasCessao && (
              <>
                <FinancialRow label="Honorários Sindicato (Apenas Cessão)" value={cessao.sindicato_total} />
                <FinancialRow label="Honorários Contador (Apenas Cessão)" value={cessao.contador_total} />
                <div className="mt-2 pt-2 border-t border-dashed border-border">
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-sm text-muted-foreground">Proposta Rclmt (Cessão)</span>
                    <span className="text-sm text-muted-foreground font-mono tabular-nums">{formatCurrencyBRL(cessao.proposta_total)}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-sm text-muted-foreground pl-2">+ Honorários Sindicato</span>
                    <span className="text-sm text-muted-foreground">+ {formatCurrencyBRL(cessao.sindicato_total)}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-sm text-muted-foreground pl-2">+ Honorários Contador</span>
                    <span className="text-sm text-muted-foreground">+ {formatCurrencyBRL(cessao.contador_total)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 mt-1 border-t border-border font-semibold">
                    <span className="text-sm text-foreground">= Desembolso Total</span>
                    <span className="text-sm text-sky-600 font-mono tabular-nums">{formatCurrencyBRL(cessao.proposta_total + cessao.sindicato_total + cessao.contador_total)}</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
